import type { EntityContextActionDetail, HoveredActorInfo, PixelPosition } from '../types';
import { getCanvasLogicalSize } from './canvasLogicalSize';

export class EntityContextMenuController {
    private entityContextMenu: HTMLDivElement | null = null;
    private entityContextIdButton: HTMLButtonElement | null = null;
    private entityContextInventoryButton: HTMLButtonElement | null = null;
    private entityContextTradeButton: HTMLButtonElement | null = null;
    private contextMenuActor: HoveredActorInfo | null = null;

    constructor(
        private readonly container: HTMLElement,
        private readonly canvas: HTMLCanvasElement,
        private readonly eventTarget: EventTarget,
    ) {}

    public registerListeners(): void {
        window.addEventListener('pointerdown', this.handleGlobalPointerDown, true);
        window.addEventListener('blur', this.handleWindowBlur);
    }

    public destroy(): void {
        window.removeEventListener('pointerdown', this.handleGlobalPointerDown, true);
        window.removeEventListener('blur', this.handleWindowBlur);
        if (this.entityContextMenu) {
            this.entityContextMenu.remove();
            this.entityContextMenu = null;
            this.entityContextIdButton = null;
            this.entityContextInventoryButton = null;
            this.entityContextTradeButton = null;
        }
        this.contextMenuActor = null;
    }

    public showEntityContextMenu(pointer: PixelPosition, actor: HoveredActorInfo): void {
        this.ensureEntityContextMenu();
        if (!this.entityContextMenu || !this.entityContextTradeButton || !this.entityContextIdButton || !this.entityContextInventoryButton) {
            return;
        }

        this.contextMenuActor = actor;
        for (const button of [this.entityContextIdButton, this.entityContextTradeButton, this.entityContextInventoryButton]) {
            button.disabled = false;
            button.style.opacity = '1';
            button.style.cursor = 'pointer';
        }
        if (actor.kind === 'mob') {
            this.entityContextTradeButton.disabled = true;
            this.entityContextTradeButton.style.opacity = '0.5';
            this.entityContextTradeButton.style.cursor = 'not-allowed';
        }

        this.entityContextMenu.style.display = 'block';
        const menuRect = this.entityContextMenu.getBoundingClientRect();
        const canvasSize = getCanvasLogicalSize(this.canvas);
        const maxLeft = Math.max(0, canvasSize.width - menuRect.width - 6);
        const maxTop = Math.max(0, canvasSize.height - menuRect.height - 6);
        this.entityContextMenu.style.left = `${Math.max(6, Math.min(pointer.x + 10, maxLeft))}px`;
        this.entityContextMenu.style.top = `${Math.max(6, Math.min(pointer.y + 8, maxTop))}px`;
    }

    public hideEntityContextMenu(): void {
        this.contextMenuActor = null;
        if (this.entityContextMenu) {
            this.entityContextMenu.style.display = 'none';
        }
    }

    private readonly handleGlobalPointerDown = (event: PointerEvent): void => {
        if (!this.entityContextMenu || this.entityContextMenu.style.display === 'none') {
            return;
        }
        const target = event.target as Node | null;
        if (target && this.entityContextMenu.contains(target)) {
            return;
        }
        this.hideEntityContextMenu();
    };

    private readonly handleWindowBlur = (): void => {
        this.hideEntityContextMenu();
    };

    private ensureEntityContextMenu(): void {
        if (this.entityContextMenu) {
            return;
        }

        const menu = document.createElement('div');
        Object.assign(menu.style, {
            position: 'absolute',
            left: '0px',
            top: '0px',
            display: 'none',
            background: 'rgba(15, 23, 42, 0.96)',
            border: '1px solid #334155',
            borderRadius: '6px',
            padding: '4px',
            zIndex: '1200',
            minWidth: '120px',
            boxShadow: '0 8px 22px rgba(0, 0, 0, 0.45)',
            pointerEvents: 'auto',
        });
        menu.addEventListener('pointerdown', (event) => event.stopPropagation());

        const makeMenuButton = (text: string): HTMLButtonElement => {
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = text;
            Object.assign(button.style, {
                width: '100%',
                textAlign: 'left',
                padding: '7px 10px',
                background: 'transparent',
                border: 'none',
                color: '#e2e8f0',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.82rem',
            });
            button.addEventListener('mouseenter', () => { button.style.background = 'rgba(96, 165, 250, 0.2)'; });
            button.addEventListener('mouseleave', () => { button.style.background = 'transparent'; });
            return button;
        };

        const openProfile = (action: 'id' | 'inventory'): void => {
            if (!this.contextMenuActor || this.contextMenuActor.kind === 'self') {
                this.hideEntityContextMenu();
                return;
            }
            const actor = this.contextMenuActor;
            const fallbackName = actor.kind === 'mob' ? this.formatMobName(actor.entityType) : 'Player';
            const name = actor.displayName?.trim() || fallbackName;
            this.eventTarget.dispatchEvent(new CustomEvent<EntityContextActionDetail>('entityContextAction', {
                detail: {
                    action,
                    profile: {
                        actorId: actor.id,
                        playerId: actor.playerId,
                        kind: actor.kind,
                        entityType: actor.entityType,
                        name,
                        hp: actor.hp,
                        maxHp: actor.maxHp,
                        stamina: actor.stamina,
                        maxStamina: actor.maxStamina,
                        rotation: actor.rotation,
                        mobInventoryValue: actor.mobInventoryValue ?? null,
                        mobInventoryItemsJson: actor.mobInventoryItemsJson ?? null,
                    }
                }
            }));
            this.hideEntityContextMenu();
        };

        const idButton = makeMenuButton('ID');
        idButton.addEventListener('click', () => openProfile('id'));

        const inventoryButton = makeMenuButton('Inventory');
        inventoryButton.addEventListener('click', () => openProfile('inventory'));

        const tradeButton = makeMenuButton('Trade');
        tradeButton.addEventListener('click', () => {
            const targetName = this.contextMenuActor?.tradeTargetName?.trim();
            if (!targetName) {
                this.hideEntityContextMenu();
                return;
            }
            this.eventTarget.dispatchEvent(new CustomEvent<EntityContextActionDetail>('entityContextAction', {
                detail: { action: 'trade', targetName }
            }));
            this.hideEntityContextMenu();
        });
        Object.assign(tradeButton.style, {
            width: '100%',
            borderTop: '1px solid rgba(148, 163, 184, 0.25)',
            marginTop: '4px',
            paddingTop: '9px',
        });

        menu.appendChild(idButton);
        menu.appendChild(inventoryButton);
        menu.appendChild(tradeButton);
        this.container.appendChild(menu);
        this.entityContextMenu = menu;
        this.entityContextIdButton = idButton;
        this.entityContextInventoryButton = inventoryButton;
        this.entityContextTradeButton = tradeButton;
    }

    private formatMobName(entityType?: string): string {
        if (!entityType || !entityType.trim()) {
            return 'Mob';
        }
        return entityType.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').toLowerCase()
            .split(' ')
            .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
            .join(' ');
    }
}
